import { useState } from "react";
import AdminHeader from "../../component/AdminHeader";
import { useForm } from "react-hook-form";
import { motion, AnimatePresence } from "framer-motion";
import { X } from "lucide-react";
import { http } from "../../axios/axios";
import { useEffect } from "react";
import ActionButtons from "../../component/AdminActionButtons";
import { useSearchParams } from "react-router-dom";
import Pagination from "../../component/Pagination";
import { GetImageUrl } from "../../helpers/GetImageUrl";

const InputField = ({
  label,
  name,
  type = "text",
  placeholder,
  register,
  errors = {},
  validation = {},
  as = "input",
  rows = 4,
  options = [],
}) => {
  const inputClass =
    "w-full border border-white/10 bg-white/5 text-white px-4 py-2 rounded-xl mt-2 outline-none focus:border-blue-500 transition";

  return (
    <div>
      <label
        htmlFor={name}
        className="mb-1.5 block text-sm font-semibold text-slate-300 md:mb-2 md:text-base"
      >
        {label}
        {validation?.required && <span className="text-red-500"> *</span>}
      </label>

      {as === "textarea" ? (
        <textarea
          id={name}
          rows={rows}
          placeholder={placeholder}
          {...register(name, validation)}
          className={`${inputClass} resize-none`}
        />
      ) : as === "select" ? (
        <select id={name} {...register(name, validation)}>
          <option value="" className="bg-[#0a0f1a] text-white">
            {placeholder}
          </option>

          {options.map((item) => (
            <option
              key={item.value}
              value={item.value}
              className="bg-[#0a0f1a] text-white"
            >
              {item.label}
            </option>
          ))}
        </select>
      ) : (
        <input
          id={name}
          type={type}
          placeholder={placeholder}
          {...register(name, validation)}
          className={inputClass}
        />
      )}

      {errors[name] && (
        <p className="mt-1 text-xs text-red-500 md:text-sm">
          {errors[name].message}
        </p>
      )}
    </div>
  );
};

const CreateCareer = ({ closeModal, onSubmit, career }) => {
  const {
    register,
    handleSubmit,
    watch,
    reset,
    formState: { errors, isSubmitting },
  } = useForm({
    shouldUnregister: true,
    defaultValues: {
      type: "Job",
    },
  });
  useEffect(() => {
    if (career) {
      reset({
        ...career,
        skills: career.skills?.join(", "),
        lastDate: career.lastDate
          ? new Date(career.lastDate).toISOString().split("T")[0]
          : "",
      });
    }
  }, [career, reset]);

  const submitForm = async (data) => {
    try {
      await onSubmit(data);
      reset();
      closeModal();
    } catch (error) {
      console.error("Career creation failed:", error);
    }
  };
  const careerType = watch("type");

  return (
    <motion.div className="fixed inset-0 bg-black/90 backdrop-blur-xl z-50">
      <motion.div
        className="fixed z-50 top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2
      w-[95%]
      h-[90%]
      bg-[#0a0f1a]
      border border-white/10
      rounded-2xl
      shadow-2xl
      overflow-hidden
      flex
      flex-col
      "
      >
        <div className="flex items-center justify-between p-4 md:px-8 border-b border-white/10 bg-white/[0.03]">
          <div>
            <h3 className="text-lg md:text-xl lg:text-2xl font-black text-white uppercase">
              {career ? "Edit Career" : "Create Career"}
            </h3>
            <p className="text-sm md:text-lg lg:text-xl">
              Add a new job posting or course listing by completing the form
              below.
            </p>
          </div>
          <button
            type="button"
            onClick={closeModal}
            className="h-10 w-10 rounded-xl bg-white/5 hover:bg-red-500 text-slate-300 flex items-center justify-center"
          >
            <X size={20} />
          </button>
        </div>
        <form
          onSubmit={handleSubmit(submitForm)}
          className="p-4 md:p-8 space-y-6 overflow-y-auto flex-1"
        >
          <div>
            <label className="block text-sm font-semibold text-slate-300 mb-2 md:text-base">
              Career Type <span className="text-red-500">*</span>
            </label>

            <div className="flex items-center gap-6">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="radio"
                  value="Job"
                  {...register("type", {
                    required: "Please select a career type",
                  })}
                />
                <span>Job</span>
              </label>

              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="radio"
                  value="Course"
                  {...register("type", {
                    required: "Please select a career type",
                  })}
                />
                <span>Course</span>
              </label>
            </div>

            {errors.type && (
              <p className="mt-1 text-sm text-red-500">
                {errors.type?.message}
              </p>
            )}
          </div>

          <InputField
            label="Title"
            name="title"
            placeholder={
              careerType === "Job"
                ? "e.g. CCTV Surveillance Officer"
                : "e.g. CCTV Surveillance Training"
            }
            register={register}
            errors={errors}
            validation={{
              required: "Title is required",
            }}
          />

          <InputField
            label="Location"
            name="location"
            placeholder={
              careerType === "Job"
                ? "e.g. Gurugram, Haryana or Remote"
                : "e.g. New Delhi Training Center"
            }
            register={register}
            errors={errors}
            validation={{
              required: "Location is required",
            }}
          />

          <InputField
            label="Contact Person"
            name="contactPerson"
            placeholder="e.g. Rahul Sharma"
            register={register}
            errors={errors}
          />

          <div>
            <InputField
              label="Skills"
              name="skills"
              placeholder="e.g. CCTV Monitoring, Patrolling, Access Control"
              register={register}
              errors={errors}
              validation={{
                required: "Skills are required",
              }}
            />

            <p className="mt-1 text-xs text-slate-400">
              Enter multiple skills separated by commas (e.g. CCTV Monitoring,
              Patrolling, Access Control).
            </p>
          </div>

          <InputField
            label="Description"
            name="description"
            placeholder={
              careerType === "Job"
                ? "Describe the job responsibilities, requirements, and expectations..."
                : "Describe the course content, learning outcomes, and eligibility..."
            }
            register={register}
            errors={errors}
            validation={{
              required: "Description is required",
            }}
            as="textarea"
          />

          <InputField
            label="Last Date"
            name="lastDate"
            type="date"
            register={register}
            errors={errors}
            validation={{
              required: "Last Date is required",
            }}
          />

          <InputField
            label="Status"
            name="status"
            as="select"
            placeholder="Select Status"
            options={[
              { value: "Draft", label: "Draft" },
              { value: "Active", label: "Active" },
              { value: "Closed", label: "Closed" },
            ]}
            register={register}
            errors={errors}
            validation={{
              required: "Status is required",
            }}
          />

          {careerType === "Job" && (
            <>
              <InputField
                label="Department"
                name="department"
                placeholder="e.g. Security Operations"
                register={register}
                errors={errors}
                validation={{
                  required: "Department is required",
                }}
              />

              <InputField
                label="Employment Type"
                name="employmentType"
                as="select"
                placeholder="Select Employment Type"
                options={[
                  { value: "Full-Time", label: "Full Time" },
                  { value: "Part-Time", label: "Part Time" },
                  { value: "Internship", label: "Internship" },
                  { value: "Contract", label: "Contract" },
                ]}
                register={register}
                errors={errors}
                validation={{
                  required: "Employment Type is required",
                }}
              />

              <InputField
                label="Experience (Years)"
                name="experience"
                type="number"
                placeholder="e.g. 2"
                register={register}
                errors={errors}
                validation={{
                  required: "Experience is required",
                  valueAsNumber: true,
                  min: {
                    value: 0,
                    message: "Experience cannot be negative",
                  },
                }}
              />

              <InputField
                label="Salary"
                name="salary"
                placeholder="e.g. ₹20,000 - ₹30,000"
                register={register}
                errors={errors}
                validation={{
                  required: "Salary is required",
                }}
              />

              <InputField
                label="Vacancies"
                name="vacancies"
                type="number"
                placeholder="e.g. 5"
                register={register}
                errors={errors}
                validation={{
                  required: "Vacancies are required",
                  valueAsNumber: true,
                  min: {
                    value: 1,
                    message: "Vacancies must be at least 1",
                  },
                }}
              />
            </>
          )}

          {careerType === "Course" && (
            <>
              <InputField
                label="Course Duration"
                name="courseDuration"
                placeholder="e.g. 2 Months"
                register={register}
                errors={errors}
                validation={{
                  required: "Course Duration is required",
                }}
              />

              <InputField
                label="Course Fee"
                name="courseFee"
                type="number"
                placeholder="e.g. 15000"
                register={register}
                errors={errors}
                validation={{
                  valueAsNumber: true,

                  required: "Course fee is required",
                }}
              />
            </>
          )}

          <div className="pt-4">
            <button
              type="submit"
              className="w-full rounded-md bg-blue-600 px-6 py-3 font-semibold text-white transition hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-60"
            >
              {isSubmitting
                ? "Saving..."
                : career
                  ? "Update Career"
                  : "Submit Career"}
            </button>
          </div>
        </form>
      </motion.div>
    </motion.div>
  );
};

const AdminCareer = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [careers, setCareers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedCareer, setSelectedCareer] = useState(null);
  const [searchParams, setSearchParams] = useSearchParams();
  const activeTab = searchParams.get("type") || "Job";
  const [page, setPage] = useState(1);
  const [pagination, setPagination] = useState({});
  const limit = 20;

  const getCareers = async () => {
    try {
      setLoading(true);

      const res = await http.get("/career", {
        params: {
          type: activeTab,
          page,
          limit,
        },
      });

      setCareers(res.data.data);
      setPagination(res.data.pagination);
    } catch (err) {
      console.log(err);
    } finally {
      setLoading(false);
    }
  };

  const fetchCareerById = async (id) => {
    try {
      setLoading(true);
      const res = await http.get(`/career/${id}`);
      setSelectedCareer(res.data?.data);
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleViewComplaint = async (id) => {
    setIsModalOpen(true);
    await fetchCareerById(id);
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this career?")) return;

    try {
      await http.delete(`/career/${id}`);

      if (careers.length === 1 && page > 1) {
        setPage((prev) => prev - 1);
      } else {
        await getCareers();
      }
    } catch (error) {
      console.error(error);
    }
  };

  useEffect(() => {
    getCareers();
  }, [activeTab, page]);

  const closeModal = () => {
    setIsModalOpen(false);
  };

  const onSubmit = async (data) => {
    const payload = {
      ...data,
      skills: data?.skills
        .split(",")
        .map((skill) => skill?.trim())
        .filter(Boolean),
    };

    try {
      if (selectedCareer) {
        await http.put(`/career/${selectedCareer?._id}`, payload);
      } else {
        await http.post("/career", payload);
      }

      await getCareers();
      closeModal();
      setSelectedCareer(null);
    } catch (error) {
      console.error(error);
    }
  };

  useEffect(() => {
    if (!searchParams.get("type")) {
      setSearchParams({ type: "Job" }, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  return (
    <div className="p-4 md:p-8 min-h-screen text-slate-200">
      <AdminHeader
        title="Manage Jobs & Courses"
        buttonText="Add Career"
        onClick={() => {
          setSelectedCareer(null);
          setIsModalOpen(true);
        }}
      />
      <div className="flex gap-3 mb-6 items-center w-full justify-center">
        {["Job", "Course"].map((tab) => (
          <button
            key={tab}
            onClick={() => {
              setPage(1);
              setSearchParams({ type: tab });
            }}
            className={`px-5 py-2 rounded-lg transition ${activeTab === tab ? "bg-blue-600 text-white" : "bg-slate-800 text-slate-300"}`}
          >
            {tab}s
          </button>
        ))}
      </div>
      <div className="space-y-4">
        {careers?.length === 0 ? (
          <div
            className="
          p-8
          rounded-2xl
          bg-white/5
          border border-white/10
          text-center
          text-slate-400
        "
          >
            No Careers found
          </div>
        ) : (
          careers.map((career) => (
            <div
              key={career?._id}
              className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4 p-5 rounded-xl bg-slate-900/40 border border-slate-800 hover:border-slate-700 hover:bg-slate-900/50 transition"
            >
              <div className="flex-1">
                <div className="flex items-center gap-3 flex-wrap">
                  {career?.title && (
                    <h3 className="text-lg font-bold text-white">
                      {career?.title}
                    </h3>
                  )}
                  {career?.status && (
                    <span
                      className={`px-2 py-1 rounded text-xs font-medium ${
                        career?.status === "Active"
                          ? "bg-green-500/20 text-green-400"
                          : career?.status === "Draft"
                            ? "bg-yellow-500/20 text-yellow-400"
                            : "bg-red-500/20 text-red-400"
                      }`}
                    >
                      {career?.status}
                    </span>
                  )}
                </div>
              </div>
              <ActionButtons
                onEdit={() => handleViewComplaint(career?._id)}
                onDelete={() => handleDelete(career?._id)}
              />
            </div>
          ))
        )}
        {careers.length > 0 && (
          <Pagination
            page={page}
            totalPages={pagination?.totalPage}
            onPrevious={() => setPage((prev) => prev - 1)}
            onNext={() => setPage((prev) => prev + 1)}
          />
        )}
      </div>
      <AnimatePresence>
        {isModalOpen && (
          <CreateCareer
            closeModal={closeModal}
            onSubmit={onSubmit}
            career={selectedCareer}
          />
        )}
      </AnimatePresence>
    </div>
  );
};
export default AdminCareer;
